import { useState } from 'react';
import { NavLink, Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../AuthContext.jsx';
import { auth, signOut } from '../../firebase.js';

const linkGroups = [
  {
    heading: 'Overview',
    items: [{ to: '/admin', label: 'Dashboard', end: true, service: null }],
  },
  {
    heading: 'Club',
    items: [
      { to: '/admin/teams', label: 'Teams', service: 'teams' },
      { to: '/admin/players', label: 'Players', service: 'players' },
      { to: '/admin/events', label: 'Events', service: 'events' },
    ],
  },
  {
    heading: 'Inbox',
    items: [
      { to: '/admin/join-requests', label: 'Join requests', service: 'joinRequests' },
      { to: '/admin/contact-messages', label: 'Contact inquiries', service: 'contactMessages' },
      { to: '/admin/bookings', label: 'Clubhouse', service: 'bookings' },
    ],
  },
  {
    heading: 'Audience',
    items: [
      { to: '/admin/contacts', label: 'Contacts', service: 'contacts' },
      { to: '/admin/lists', label: 'Lists', service: 'lists' },
      { to: '/admin/campaigns', label: 'Campaigns', service: 'campaigns' },
    ],
  },
  {
    heading: 'Content',
    items: [
      { to: '/admin/news-posts', label: 'News posts', service: 'newsPosts' },
      { to: '/admin/meetings', label: 'Meetings & minutes', service: 'meetings' },
    ],
  },
  {
    heading: 'Shop',
    items: [{ to: '/admin/products', label: 'Products', service: 'products' }],
  },
  {
    heading: 'Access',
    items: [
      { to: '/admin/users', label: 'Users', service: 'users' },
      { to: '/admin/signup-fields', label: 'Signup fields', service: 'signupFields' },
    ],
  },
  {
    heading: 'Settings',
    items: [
      { to: '/admin/settings/stripe', label: 'Stripe', service: 'settings' },
    ],
  },
];

const allLinks = linkGroups.flatMap((g) => g.items);

export default function AdminLayout() {
  const [open, setOpen] = useState(false);
  const { user, can } = useAuth();
  const { pathname } = useLocation();
  const current = allLinks.find((l) => (l.end ? pathname === l.to : pathname.startsWith(l.to)));

  // Filter each group to only show items the current admin can read.
  const visibleGroups = linkGroups
    .map((g) => ({ ...g, items: g.items.filter((l) => !l.service || can(l.service, 'read')) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="min-h-screen bg-canvas">
      {/* Top bar */}
      <header className="sticky top-0 z-20 bg-canvas/90 backdrop-blur-lg border-b border-line">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setOpen(true)}
              className="md:hidden h-9 w-9 -ml-2 flex flex-col items-center justify-center gap-[4px]"
              aria-label="Open menu"
            >
              <span className="h-[1.5px] w-5 bg-ink" />
              <span className="h-[1.5px] w-5 bg-ink" />
              <span className="h-[1.5px] w-5 bg-ink" />
            </button>
            <Link to="/" className="flex items-baseline gap-2 shrink-0">
              <span className="font-display text-xl font-semibold tracking-tight">Blaze</span>
              <span className="eyebrow text-brand">Admin</span>
            </Link>
            <span className="hidden md:inline text-ink-3">/</span>
            <span className="hidden md:inline text-sm text-ink-2 truncate">{current?.label || 'Admin'}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-xs text-ink-3 truncate max-w-[160px]">{user?.email}</span>
            <button onClick={() => signOut(auth)} className="btn-ghost btn-sm">
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-10 md:grid md:grid-cols-[220px_1fr] md:gap-10">
        {/* Desktop sidebar */}
        <aside className="hidden md:block">
          <nav className="sticky top-24 flex flex-col gap-5">
            {visibleGroups.map((g) => (
              <div key={g.heading}>
                <div className="eyebrow mb-2 px-3">{g.heading}</div>
                {g.items.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.end}
                    className={({ isActive }) =>
                      `py-2 px-3 text-sm rounded-md transition-colors block ${
                        isActive ? 'bg-brand/10 text-brand-ink font-medium' : 'text-ink-2 hover:text-ink hover:bg-line-soft/60'
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>
        </aside>

        {/* Mobile drawer */}
        {open && (
          <>
            <div className="fixed inset-0 bg-ink/40 backdrop-blur-[2px] z-40 md:hidden" onClick={() => setOpen(false)} />
            <aside className="fixed top-0 left-0 h-full w-[78%] max-w-xs bg-canvas border-r border-line z-50 md:hidden p-5 animate-[slideRight_220ms_cubic-bezier(0.2,0.8,0.2,1)]">
              <div className="flex items-baseline gap-2 mb-8">
                <span className="font-display text-2xl font-semibold">Blaze</span>
                <span className="eyebrow text-brand">Admin</span>
              </div>
              <nav className="flex flex-col gap-4">
                {visibleGroups.map((g) => (
                  <div key={g.heading}>
                    <div className="eyebrow mb-1 px-3">{g.heading}</div>
                    {g.items.map((l) => (
                      <NavLink
                        key={l.to}
                        to={l.to}
                        end={l.end}
                        onClick={() => setOpen(false)}
                        className={({ isActive }) =>
                          `py-2.5 px-3 text-sm rounded-md transition-colors block ${
                            isActive ? 'bg-brand/10 text-brand-ink font-medium' : 'text-ink-2'
                          }`
                        }
                      >
                        {l.label}
                      </NavLink>
                    ))}
                  </div>
                ))}
              </nav>
            </aside>
            <style>{`@keyframes slideRight { from { transform: translateX(-100%); } to { transform: translateX(0); } }`}</style>
          </>
        )}

        <main className="min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
